import { createSlice } from "@reduxjs/toolkit";
import {
  getLexBots,
  getLexIntents,
  getLexUtterances,
  getLexSlots,
  getLexSlotTypes,
  getLexSlotTypeDetail,
  getLexMissedUtterances,
  resetLexBotDetail,
  resetLexBots,
} from "../services/lexBots";
import type {
  LexBotInfo,
  LexIntentSummary,
  LexSlotSummary,
  LexSlotTypeSummary,
  LexSlotTypeDetailData,
  LexMissedUtteranceCount,
} from "../features/content/manager/table/lexBots/types";

interface LexBotsState {
  bots: LexBotInfo[];
  botsLoading: boolean;

  intents: LexIntentSummary[];
  intentsLoading: boolean;

  utterances: string[];
  utterancesLoading: boolean;

  slots: LexSlotSummary[];
  slotsLoading: boolean;

  slotTypes: LexSlotTypeSummary[];
  slotTypesLoading: boolean;

  slotTypeDetail: LexSlotTypeDetailData | null;
  slotTypeDetailLoading: boolean;
  slotTypeDetailError: boolean;

  missedUtterances: LexMissedUtteranceCount[];
  missedUtterancesLoading: boolean;
  missedUtterancesError: boolean;

  isError: boolean;
  errorMessage: string;
}

const initialState: LexBotsState = {
  bots: [],
  botsLoading: false,

  intents: [],
  intentsLoading: false,

  utterances: [],
  utterancesLoading: false,

  slots: [],
  slotsLoading: false,

  slotTypes: [],
  slotTypesLoading: false,

  slotTypeDetail: null,
  slotTypeDetailLoading: false,
  slotTypeDetailError: false,

  missedUtterances: [],
  missedUtterancesLoading: false,
  missedUtterancesError: false,

  isError: false,
  errorMessage: "",
};

const lexBotsSlice = createSlice({
  name: "lexBots",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // resetLexBotDetail: clear modal data, keep bots list
      .addCase(resetLexBotDetail, (state) => {
        state.intents = [];
        state.intentsLoading = false;
        state.utterances = [];
        state.utterancesLoading = false;
        state.slots = [];
        state.slotsLoading = false;
        state.slotTypes = [];
        state.slotTypesLoading = false;
        state.slotTypeDetail = null;
        state.slotTypeDetailLoading = false;
        state.slotTypeDetailError = false;
        state.missedUtterances = [];
        state.missedUtterancesLoading = false;
        state.missedUtterancesError = false;
        state.isError = false;
        state.errorMessage = "";
      })

      // resetLexBots: clear everything
      .addCase(resetLexBots, () => initialState)

      // getLexBots
      .addCase(getLexBots.pending, (state) => {
        state.botsLoading = true;
        state.isError = false;
        state.errorMessage = "";
        state.bots = [];
      })
      .addCase(getLexBots.fulfilled, (state, action) => {
        state.botsLoading = false;
        state.bots = action.payload.data?.bots ?? [];
      })
      .addCase(getLexBots.rejected, (state, action) => {
        state.botsLoading = false;
        state.isError = true;
        state.errorMessage =
          action.payload ?? action.error.message ?? "Error loading Lex bots";
      })

      // getLexIntents
      .addCase(getLexIntents.pending, (state) => {
        state.intentsLoading = true;
        state.intents = [];
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(getLexIntents.fulfilled, (state, action) => {
        state.intentsLoading = false;
        state.intents = action.payload.data?.intentSummaries ?? [];
      })
      .addCase(getLexIntents.rejected, (state) => {
        state.intentsLoading = false;
      })

      // getLexUtterances
      .addCase(getLexUtterances.pending, (state) => {
        state.utterancesLoading = true;
        state.utterances = [];
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(getLexUtterances.fulfilled, (state, action) => {
        state.utterancesLoading = false;
        state.utterances = action.payload.data?.utterances ?? [];
      })
      .addCase(getLexUtterances.rejected, (state) => {
        state.utterancesLoading = false;
      })

      // getLexSlots
      .addCase(getLexSlots.pending, (state) => {
        state.slotsLoading = true;
        state.slots = [];
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(getLexSlots.fulfilled, (state, action) => {
        state.slotsLoading = false;
        state.slots = action.payload.data?.slotSummaries ?? [];
      })
      .addCase(getLexSlots.rejected, (state) => {
        state.slotsLoading = false;
      })

      // getLexSlotTypes
      .addCase(getLexSlotTypes.pending, (state) => {
        state.slotTypesLoading = true;
        state.slotTypes = [];
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(getLexSlotTypes.fulfilled, (state, action) => {
        state.slotTypesLoading = false;
        state.slotTypes = action.payload.data?.slotTypeSummaries ?? [];
      })
      .addCase(getLexSlotTypes.rejected, (state) => {
        state.slotTypesLoading = false;
      })

      // getLexSlotTypeDetail
      .addCase(getLexSlotTypeDetail.pending, (state) => {
        state.slotTypeDetailLoading = true;
        state.slotTypeDetailError = false;
        state.slotTypeDetail = null;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(getLexSlotTypeDetail.fulfilled, (state, action) => {
        state.slotTypeDetailLoading = false;
        state.slotTypeDetailError = false;
        state.slotTypeDetail = action.payload.data ?? null;
      })
      .addCase(getLexSlotTypeDetail.rejected, (state) => {
        state.slotTypeDetailLoading = false;
        state.slotTypeDetailError = true;
      })

      // getLexMissedUtterances
      .addCase(getLexMissedUtterances.pending, (state) => {
        state.missedUtterancesLoading = true;
        state.missedUtterancesError = false;
        state.missedUtterances = [];
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(getLexMissedUtterances.fulfilled, (state, action) => {
        state.missedUtterancesLoading = false;
        state.missedUtterancesError = false;
        state.missedUtterances = action.payload.data?.missedUtterances ?? [];
      })
      .addCase(getLexMissedUtterances.rejected, (state) => {
        state.missedUtterancesLoading = false;
        state.missedUtterancesError = true;
      });
  },
});

export default lexBotsSlice.reducer;
